import { useDebounceFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import { captureException } from '@/errors';
import { useFlashesStore, useReportsStore } from '@/stores';
import { ReportSlackChannel } from '@/types/reports';

const SEARCH_DEBOUNCE_MS = 250;

export default function useSlackChannelSearch() {
  const reportsStore = useReportsStore();
  const { slackApp } = storeToRefs(reportsStore);
  const flashesStore = useFlashesStore();

  const channelSearchResults = ref<ReportSlackChannel[] | null>(null);
  const customLookupChannels = ref<ReportSlackChannel[]>([]);
  const isFetchingChannels = ref(false);
  const isLookingUpChannel = ref(false);
  const channelSearchQuery = ref('');
  let latestSearchId = 0;

  const syncStatus = computed(() => slackApp.value?.sync_status);
  const syncedAt = computed(() => slackApp.value?.synced_at ?? undefined);

  const cachedChannels = computed<ReportSlackChannel[]>(
    () => channelSearchResults.value ?? slackApp.value?.channel_list ?? [],
  );

  const needsReauth = computed(() => slackApp.value?.needs_reauth);
  const isChannelListSyncing = computed(
    () =>
      !!slackApp.value?.is_enabled &&
      (syncStatus.value === 'syncing' || !syncedAt.value),
  );
  const showSyncingState = computed(
    () => isChannelListSyncing.value && !needsReauth.value,
  );
  const hasNoSyncedChannels = computed(
    () =>
      !needsReauth.value &&
      !!syncedAt.value &&
      !slackApp.value?.channel_list?.length,
  );
  const hasSyncError = computed(
    () => !needsReauth.value && syncStatus.value === 'error',
  );

  async function searchChannels(search: string) {
    const searchId = ++latestSearchId;
    isFetchingChannels.value = true;
    try {
      const { data, error } = await crossbeamApi.GET('/v0.1/slack-app', {
        params: { query: search ? { search } : {} },
      });
      if (searchId !== latestSearchId) return;
      if (error || !data) {
        captureException(error ?? new Error('Slack channel search failed'));
        flashesStore.addErrorFlash({
          message: "Couldn't search Slack channels, please try again",
        });
        return;
      }
      channelSearchResults.value = data.channel_list ?? [];
    } finally {
      if (searchId === latestSearchId) isFetchingChannels.value = false;
    }
  }

  const debouncedSearchChannels = useDebounceFn(
    (search: string) => searchChannels(search),
    SEARCH_DEBOUNCE_MS,
  );

  function onChannelSearch(query: string) {
    channelSearchQuery.value = query.replace(/^#/, '');
    debouncedSearchChannels(channelSearchQuery.value.trim());
  }

  function resetSearch() {
    channelSearchQuery.value = '';
    debouncedSearchChannels('');
  }

  function clearCustomChannels() {
    customLookupChannels.value = [];
  }

  function isValidSlackChannelName(name: string) {
    return /^[a-z0-9][a-z0-9_-]*$/.test(name) && name.length <= 80;
  }

  async function lookUpCustomChannel(
    name: string,
    { requireShared = false }: { requireShared?: boolean } = {},
  ): Promise<ReportSlackChannel | null> {
    if (!isValidSlackChannelName(name)) {
      flashesStore.addErrorFlash({
        message:
          'Channel names may only contain lowercase letters, numbers, hyphens, and underscores',
      });
      return null;
    }
    isLookingUpChannel.value = true;
    try {
      const { data, error, response } = await crossbeamApi.GET(
        '/v0.1/slack-app/channel-by-name',
        { params: { query: { name } } },
      );
      const status = response.status;
      if (error || !data) {
        if (status === 404) {
          flashesStore.addErrorFlash({
            message: `Channel "${name}" not found`,
          });
        } else {
          captureException(
            error ?? new Error(`Channel lookup failed (status ${status})`),
          );
          flashesStore.addErrorFlash({
            message: `Couldn't verify channel "${name}", please try again`,
          });
        }
        return null;
      }
      if (requireShared && data.channel_type !== 'shared-channel') {
        flashesStore.addErrorFlash({
          message:
            'Only shared Slack channels can be used for partner notifications',
        });
        return null;
      }
      if (
        !customLookupChannels.value.some(
          (ch: ReportSlackChannel) => ch.id === data.id,
        )
      ) {
        customLookupChannels.value = [...customLookupChannels.value, data];
      }
      return data;
    } finally {
      isLookingUpChannel.value = false;
    }
  }

  return {
    isFetchingChannels,
    isLookingUpChannel,
    channelSearchQuery,
    cachedChannels,
    customLookupChannels,
    searchChannels,
    onChannelSearch,
    resetSearch,
    clearCustomChannels,
    lookUpCustomChannel,
    isChannelListSyncing,
    showSyncingState,
    hasNoSyncedChannels,
    hasSyncError,
  };
}
